/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef } from 'react';
import { format, isPast, parseISO } from 'date-fns';
import useBreakpoint, { canHover } from '../hooks/useBreakpoint';

const INVESTOR_COLUMNS = [
  { key: 'location',        label: 'Location',           default: true },
  { key: 'fund_type',       label: 'Type',               default: true },
  { key: 'focus',           label: 'Focus',              default: true },
  { key: 'priority',        label: 'Priority',           default: true },
  { key: 'check_size',      label: 'Typical Check Size', default: true },
  { key: 'fit_for_syncrea', label: 'Fit for Syncrea',    default: true },
  { key: 'email',           label: 'Email',              default: true },
  { key: 'linkedin',        label: 'LinkedIn',           default: true },
  { key: 'contact_name',    label: 'Primary Contact',    default: true },
  { key: 'contact_type',    label: 'Type of Contact',    default: true },
  { key: 'notes',           label: 'Summary',            default: true },
  { key: 'stage',           label: 'Stage',              default: true },
  { key: 'status',          label: 'Status',             default: true },
  { key: 'phone',           label: 'Phone',              default: true },
  { key: 'is_lead',         label: 'Lead',               default: true },
  { key: 'owner',           label: 'Owner',              default: true },
  { key: 'activity',        label: 'Activity',           default: true },
  { key: 'activity_date',   label: 'Due Date',           default: true },
  { key: 'last_updated_by', label: 'By',                 default: true },
  { key: 'last_updated_at', label: 'Last Updated',       default: true },
];

const PARTNER_COLUMNS = [
  { key: 'location',         label: 'Location',         default: true },
  { key: 'stage',            label: 'Stage',            default: true },
  { key: 'status',           label: 'Status',           default: true },
  { key: 'priority',         label: 'Priority',         default: true },
  { key: 'cas_maturity',     label: 'CAS',              default: true },
  { key: 'managing_partner', label: 'Managing Partner', default: true },
  { key: 'contact_name',     label: 'Contact',          default: true },
  { key: 'activity',         label: 'Activity',         default: true },
  { key: 'activity_date',    label: 'Due Date',         default: true },
  { key: 'notes',            label: 'Notes',            default: true },
  { key: 'owner',            label: 'Owner',            default: true },
  { key: 'contact_type',     label: 'Type of Contact',  default: true },
  { key: 'employee_count',   label: 'Staff',            default: true },
  { key: 'client_count',     label: 'Clients',          default: true },
  { key: 'firm_type',        label: 'Firm Type',        default: true },
  { key: 'uses_qbo',         label: 'QBO',              default: true },
  { key: 'website',          label: 'Website',          default: true },
  { key: 'email',            label: 'Email',            default: true },
  { key: 'last_updated_by',  label: 'By',               default: true },
  { key: 'last_updated_at',  label: 'Last Updated',     default: true },
];

function stageBadge(stage) {
  const colors = {
    'Prospect':       '#7A8FAA', 'Contacted':      '#2EA2EB',
    'Intro Call':     '#A78BFA', 'Deep Dive':      '#FBBF24',
    'Due Diligence':  '#FB923C', 'Evaluating':     '#FBBF24',
    'Pilot':          '#FB923C', 'Term Sheet':     '#34D399',
    'Closed':         '#10B981', 'Active Partner': '#10B981',
    'Passed':         '#F87171', 'Churned':        '#F87171',
    'On Hold':        '#6B7280',
  };
  return <span style={{ color: colors[stage] || '#7A8FAA', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{stage || '—'}</span>;
}

function statusBadge(status) {
  const map = {
    'New':          { bg: 'rgba(122,143,170,0.1)', color: '#7A8FAA' },
    'Active':       { bg: 'var(--success-bg)',      color: 'var(--success)' },
    'High Priority':{ bg: 'var(--warning-bg)',      color: 'var(--warning)' },
    'Paused':       { bg: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)' },
    'Closed':       { bg: 'rgba(248,113,113,0.08)', color: 'var(--danger)' },
  };
  const c = map[status] || map['New'];
  return <span style={{ background: c.bg, color: c.color, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{status}</span>;
}

function priorityBadge(val) {
  const colors = { High: '#F87171', Medium: '#FBBF24', Low: '#7A8FAA' };
  return <span style={{ color: colors[val] || '#7A8FAA', fontSize: 12, fontWeight: 600 }}>{val || '—'}</span>;
}

function maturityBadge(val) {
  if (!val) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
  const colors = { High: '#34D399', Medium: '#FBBF24', Low: '#F87171' };
  return <span style={{ color: colors[val] || '#7A8FAA', fontSize: 12, fontWeight: 600 }}>{val}</span>;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return format(typeof d === 'string' ? parseISO(d) : d, 'MMM d, yy'); } catch { return d; }
}

function fmtDateLong(d) {
  if (!d) return '—';
  try { return format(typeof d === 'string' ? parseISO(d) : d, 'MMM d, yyyy'); } catch { return d; }
}

function isOverdue(dateStr, closed) {
  if (!dateStr || closed) return false;
  try { return isPast(parseISO(dateStr.split('T')[0] + 'T23:59:59')); } catch { return false; }
}

function compareValues(a, b, key) {
  const va = a[key], vb = b[key];
  const aE = va === null || va === undefined || va === '';
  const bE = vb === null || vb === undefined || vb === '';
  if (aE && bE) return 0;
  if (aE) return 1;
  if (bE) return -1;
  if (typeof va === 'boolean' || typeof vb === 'boolean') return (va ? 1 : 0) - (vb ? 1 : 0);
  const na = Number(va), nb = Number(vb);
  if (!isNaN(na) && !isNaN(nb)) return na - nb;
  return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true });
}

function renderCell(key, r, overdue) {
  switch (key) {
    case 'stage':          return stageBadge(r.stage);
    case 'status':         return statusBadge(r.status);
    case 'priority':       return priorityBadge(r.priority);
    case 'cas_maturity':   return maturityBadge(r.cas_maturity);
    case 'is_lead':        return r.is_lead ? <span style={{ color: '#34D399', fontWeight: 600 }}>✓ Lead</span> : '—';
    case 'uses_qbo':       return r.uses_qbo ? <span style={{ color: '#34D399', fontWeight: 600 }}>✓</span> : '—';
    case 'check_size':     return r.check_size ? <span style={{ color: 'var(--text)' }}>{r.check_size}</span> : '—';
    case 'linkedin':
      return r.linkedin
        ? <a href={r.linkedin} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--primary)', fontSize: 11 }}>View</a>
        : '—';
    case 'activity_date':
      return r.activity_date
        ? <span style={{ color: overdue ? 'var(--overdue)' : 'var(--text-muted)', fontWeight: overdue ? 600 : 400, whiteSpace: 'nowrap' }}>
            {overdue ? '⚠ ' : ''}{fmtDate(r.activity_date)}
          </span>
        : '—';
    case 'activity':
      return r.activity
        ? <span style={{ color: overdue ? 'var(--overdue)' : 'var(--text)' }}>{r.activity}</span>
        : <span style={{ color: 'var(--text-dim)' }}>—</span>;
    case 'website':
      return r.website
        ? <a href={r.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--primary)', fontSize: 11 }}>
            {r.website.replace(/https?:\/\//, '').replace(/\/$/, '')}
          </a>
        : '—';
    case 'last_updated_at':
      return <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>{fmtDateLong(r.last_updated_at)}</span>;
    default:
      return <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 160 }}>{r[key] || '—'}</span>;
  }
}

function ColumnsPicker({ cols, visible, onToggle, show, onToggleShow }) {
  return (
    <div style={{ position: 'relative' }}>
      <button className="col-picker-btn" onClick={onToggleShow} style={{
        background: show ? 'var(--primary-dim)' : 'var(--bg-card)',
        border: `1px solid ${show ? 'var(--primary-border)' : 'var(--border-med)'}`,
        borderRadius: 'var(--radius-sm)', color: show ? 'var(--primary)' : 'var(--text-muted)',
        fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 500,
        padding: '5px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        ⊞ Columns ({visible.size + 1})
      </button>
      {show && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)',
          zIndex: 100, padding: '10px 0', minWidth: 210, maxHeight: '60vh', overflowY: 'auto',
        }}>
          <div style={{ padding: '4px 14px 8px', fontSize: 10, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Show / Hide Columns
          </div>
          <div className="col-picker-row" style={{ padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="col-picker-box" style={{ width: 16, height: 16, borderRadius: 4, background: 'var(--primary)', border: '1px solid var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontSize: 10 }}>✓</span>
            </div>
            <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>Firm Name (always)</span>
          </div>
          {cols.map(col => {
            const on = visible.has(col.key);
            return (
              <div key={col.key} className="col-picker-row" onClick={() => onToggle(col.key)}
                style={{ padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                onMouseEnter={e => { if (canHover()) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { if (canHover()) e.currentTarget.style.background = 'transparent'; }}
              >
                <div className="col-picker-box" style={{ width: 16, height: 16, borderRadius: 4, background: on ? 'var(--primary)' : 'var(--bg-card)', border: `1px solid ${on ? 'var(--primary)' : 'var(--border-med)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {on && <span style={{ color: '#fff', fontSize: 10 }}>✓</span>}
                </div>
                <span style={{ fontSize: 13, color: on ? 'var(--text)' : 'var(--text-muted)' }}>{col.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function RecordsTable({ records, loading, onEdit, onBulkDelete, pipeline }) {
  const ALL_COLUMNS = pipeline === 'Investor' ? INVESTOR_COLUMNS : PARTNER_COLUMNS;
  const [visible,    setVisible]    = useState(() => new Set(ALL_COLUMNS.filter(c => c.default).map(c => c.key)));
  const [showPicker, setShowPicker] = useState(false);
  const [selected,   setSelected]   = useState(new Set());
  const [sortKey,    setSortKey]    = useState(null);
  const [sortDir,    setSortDir]    = useState('asc');
  const scrollRef = useRef();
  const isPhone = useBreakpoint() === 'phone';

  React.useEffect(() => {
    setVisible(new Set(ALL_COLUMNS.filter(c => c.default).map(c => c.key)));
  }, [pipeline]);

  React.useEffect(() => {
    setSelected(new Set());
  }, [pipeline]);

  // Drop selected ids that no longer exist (e.g. deleted by someone else).
  React.useEffect(() => {
    setSelected(prev => {
      if (!prev.size) return prev;
      const ids = new Set(records.map(r => r.id));
      const next = new Set(Array.from(prev).filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [records]);

  const toggleCol       = (key) => setVisible(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleSelect    = (id, e) => { e.stopPropagation(); setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleSelectAll = () => setSelected(selected.size === records.length ? new Set() : new Set(records.map(r => r.id)));
  const handleBulkDelete = async () => { const success = await onBulkDelete(Array.from(selected)); if (success) setSelected(new Set()); };
  const scrollH = (dir) => scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  const scrollV = (dir) => scrollRef.current?.scrollBy({ top: dir * 150, behavior: 'smooth' });
  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const visibleCols   = ALL_COLUMNS.filter(c => visible.has(c.key));
  const allSelected   = records.length > 0 && selected.size === records.length;
  const someSelected  = selected.size > 0;
  const sortedRecords = sortKey
    ? [...records].sort((a, b) => sortDir === 'asc' ? compareValues(a, b, sortKey) : compareValues(b, a, sortKey))
    : records;

  // ── Cell styles — mirrors CTOTracker's frozenTh / normalTh / frozenTd pattern ──
  // Two frozen columns: checkbox (left: 0) and firm name (left: 40).
  // borderCollapse: 'separate' on the table is what makes sticky work correctly.
  const checkTh   = { padding: '8px 10px', textAlign: 'center',  fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, left: 0,  zIndex: 4, width: 40,  minWidth: 40 };
  const frozenTh  = { padding: '8px 10px', textAlign: 'left',    fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, left: 40, zIndex: 4, width: 175, minWidth: 175, borderRight: '2px solid var(--border-med)' };
  const normalTh  = { padding: '8px 10px', textAlign: 'left',    fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, zIndex: 2 };
  const checkTd   = { padding: '8px 10px', textAlign: 'center',  borderBottom: '1px solid var(--border)', position: 'sticky', left: 0,  zIndex: 1, width: 40,  minWidth: 40 };
  const frozenTd  = { padding: '8px 10px', textAlign: 'left',    borderBottom: '1px solid var(--border)', position: 'sticky', left: 40, zIndex: 1, width: 175, minWidth: 175, maxWidth: 175, fontWeight: 600, color: 'var(--text)', borderRight: '2px solid var(--border-med)' };
  const scrollBtn = { background: 'var(--bg-card2)', border: '1px solid var(--border-med)', borderRadius: 6, color: 'var(--text-muted)', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>;

  // ── Phone: tappable card list (same records, same sort state, same edit drawer) ──
  if (isPhone) {
    const sortOptions = [
      ['firm_name',       pipeline === 'Investor' ? 'Investor' : 'Firm Name'],
      ['stage',           'Stage'],
      ['status',          'Status'],
      ['priority',        'Priority'],
      ['activity_date',   'Due Date'],
      ['last_updated_at', 'Last Updated'],
    ];
    const phoneCtl = { background: 'var(--bg-card)', border: '1px solid var(--border-med)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', minHeight: 44 };
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <label htmlFor="phone-sort-select" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
            Sort by
          </label>
          <select
            id="phone-sort-select" value={sortKey || ''}
            onChange={e => setSortKey(e.target.value || null)}
            style={{ ...phoneCtl, flex: 1, minWidth: 0, padding: '0 10px' }}
          >
            <option value="">Default order</option>
            {sortOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <button
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            disabled={!sortKey}
            aria-label={sortDir === 'asc' ? 'Ascending, tap for descending' : 'Descending, tap for ascending'}
            style={{ ...phoneCtl, minWidth: 44, fontSize: 18, cursor: sortKey ? 'pointer' : 'not-allowed', opacity: sortKey ? 1 : 0.5 }}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        {!records.length ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No records found. Use Quick Add to get started.
          </div>
        ) : sortedRecords.map(r => {
          const overdue = isOverdue(r.activity_date, r.closed);
          return (
            <div
              key={r.id} role="button" tabIndex={0}
              onClick={() => onEdit(r)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(r); } }}
              style={{
                background: overdue ? 'rgba(251,146,60,0.04)' : 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderLeft: `3px solid ${overdue ? 'var(--overdue)' : 'transparent'}`,
                borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', wordBreak: 'break-word' }}>{r.firm_name}</div>
              <div className="phone-card-badges" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 6 }}>
                {stageBadge(r.stage)}
                {statusBadge(r.status)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginTop: 8, fontSize: 14 }}>
                <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{renderCell('activity', r, overdue)}</div>
                <div style={{ flexShrink: 0 }}>{renderCell('activity_date', r, overdue)}</div>
              </div>
            </div>
          );
        })}

        <div style={{ padding: '4px 2px 0', fontSize: 12, color: 'var(--text-dim)' }}>
          {records.length} record{records.length !== 1 ? 's' : ''} · Tap a card to edit
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexShrink: 0 }}>
        <div>
          {someSelected && (
            <button className="bulk-del" onClick={handleBulkDelete} style={{ background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 600, padding: '5px 14px', cursor: 'pointer' }}>
              🗑 Delete {selected.size} selected
            </button>
          )}
        </div>
        <ColumnsPicker cols={ALL_COLUMNS} visible={visible} onToggle={toggleCol} show={showPicker} onToggleShow={() => setShowPicker(s => !s)} />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        {/* Scroll controls */}
        <div className="scroll-rail" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 6px', flexShrink: 0 }}>
          <button style={scrollBtn} onClick={() => scrollV(-1)}>▲</button>
          <button style={scrollBtn} onClick={() => scrollV(1)}>▼</button>
          <div style={{ width: 20, height: 1, background: 'var(--border)', margin: '2px 0' }} />
          <button style={scrollBtn} onClick={() => scrollH(-1)}>◀</button>
          <button style={scrollBtn} onClick={() => scrollH(1)}>▶</button>
        </div>

        {/* Table */}
        <div style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {!records.length ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, flex: 1 }}>
              No records found. Use Quick Add to get started.
            </div>
          ) : (
            /* Single table — checkbox and firm name columns are frozen via CSS sticky
               on the same <tr> as every other column, so row heights are always
               identical across the full row with no synchronization needed. */
            <div ref={scrollRef} style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', position: 'relative', minWidth: 0 }}>
              <table className="touch-table" style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th className="rt-check" style={checkTh}>
                      <label className="tap-area">
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer', width: 14, height: 14, accentColor: 'var(--primary)' }} />
                      </label>
                    </th>
                    <th className="rt-frozen" style={{ ...frozenTh, cursor: 'pointer' }} onClick={() => handleSort('firm_name')}>
                      {pipeline === 'Investor' ? 'Investor' : 'Firm Name'}
                      {sortKey === 'firm_name' && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    {visibleCols.map(col => (
                      <th key={col.key} style={{ ...normalTh, cursor: 'pointer' }} onClick={() => handleSort(col.key)}>
                        {col.label}
                        {sortKey === col.key && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRecords.map((r, idx) => {
                    const overdue   = isOverdue(r.activity_date, r.closed);
                    const isChecked = selected.has(r.id);
                    const rowBg     = isChecked ? 'rgba(46,162,235,0.08)' : overdue ? 'rgba(251,146,60,0.04)' : idx % 2 === 0 ? 'var(--bg-card)' : '#FFFFFF';
                    return (
                      <tr key={r.id}
                        onClick={() => onEdit(r)}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={e => { if (canHover()) Array.from(e.currentTarget.cells).forEach(cell => { cell.style.background = isChecked ? 'rgba(46,162,235,0.12)' : 'var(--bg-hover)'; }); }}
                        onMouseLeave={e => { if (canHover()) Array.from(e.currentTarget.cells).forEach(cell => { cell.style.background = rowBg; }); }}
                      >
                        <td className="rt-check" onClick={e => toggleSelect(r.id, e)}
                          style={{ ...checkTd, background: rowBg, borderLeft: isChecked ? '3px solid var(--primary)' : overdue ? '3px solid var(--overdue)' : '3px solid transparent' }}>
                          <input type="checkbox" checked={isChecked} onChange={() => {}} onClick={e => toggleSelect(r.id, e)} style={{ cursor: 'pointer', width: 14, height: 14, accentColor: 'var(--primary)' }} />
                        </td>
                        <td className="rt-frozen" style={{ ...frozenTd, background: rowBg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.firm_name}
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                              {renderCell(col.key, r, overdue)}
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '7px 14px', fontSize: 11, color: 'var(--text-dim)', borderTop: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
            <span>
              {records.length} record{records.length !== 1 ? 's' : ''}
              {someSelected && <span style={{ color: 'var(--primary)', marginLeft: 8 }}>· {selected.size} selected</span>}
              {' · '}Click any row to edit
            </span>
            <span>{visibleCols.length + 1} columns</span>
          </div>
        </div>
      </div>
    </div>
  );
}
